import { memo, ReactElement } from 'react';

import { Trans, useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import {
  Box,
  Chip,
  StaticCurrencySpan,
  StaticNumericSpan,
  Typography,
} from '@myn/mui';

import { DECIMAL_LIMIT } from '../../../constants/campaignBuilder.js';
import type { BuyingPlanInfo } from '../../../types/buyingPlanInfo.js';

type BudgetItem = {
  key: keyof Pick<
    BuyingPlanInfo,
    'campaignBudget' | 'campaignAllocatedBudget' | 'campaignUnallocatedBudget'
  >;
  title: string;
  chipLabel: ReactElement;
};

type Props = {
  selectedPlan: BuyingPlanInfo | null;
  currency: string | null;
  variant?: 'default' | 'chip';
};

const BudgetList = memo(
  ({ selectedPlan, currency, variant = 'default' }: Props) => {
    const { t } = useTranslation('@myn/planning');

    const getBudgetComponent = (
      fieldName: keyof Pick<
        BuyingPlanInfo,
        | 'campaignBudget'
        | 'campaignAllocatedBudget'
        | 'campaignUnallocatedBudget'
      >,
    ) => {
      const budget = selectedPlan?.[fieldName] ?? 0;

      if (!budget) {
        return <span />;
      }

      if (!currency) {
        return (
          <StaticNumericSpan
            number={budget}
            options={{ minDecimals: DECIMAL_LIMIT.BUDGET }}
          />
        );
      }

      return (
        <StaticCurrencySpan
          currency={currency}
          number={budget}
          options={{ minDecimals: DECIMAL_LIMIT.BUDGET }}
        />
      );
    };

    const budgets: BudgetItem[] = [
      {
        key: 'campaignBudget',
        title: t('Campaign budget'),
        chipLabel: (
          <Trans
            i18nKey="Campaign budget: <Budget/>"
            t={t}
            components={{
              Budget: getBudgetComponent('campaignBudget'),
            }}
          />
        ),
      },
      {
        key: 'campaignAllocatedBudget',
        title: t('Planned budget'),
        chipLabel: (
          <Trans
            i18nKey="Planned budget: <Budget/>"
            t={t}
            components={{
              Budget: getBudgetComponent('campaignAllocatedBudget'),
            }}
          />
        ),
      },
      {
        key: 'campaignUnallocatedBudget',
        title: t('Unplanned budget'),
        chipLabel: (
          <Trans
            i18nKey="Unplanned budget: <Budget/>"
            t={t}
            components={{
              Budget: getBudgetComponent('campaignUnallocatedBudget'),
            }}
          />
        ),
      },
    ];

    if (variant === 'chip') {
      return (
        <>
          {budgets.map(({ key, chipLabel }) => (
            <Chip
              key={key}
              variant="outlined"
              size="medium"
              color="secondary"
              label={chipLabel}
            />
          ))}
        </>
      );
    }

    return (
      <>
        {budgets.map(({ key, title }) => (
          <BudgetBlock key={key}>
            <Typography
              variant="caption02"
              color={(theme) => theme.palette.text.secondary}
            >
              {title}
            </Typography>
            <Typography variant="bodyLong02">
              {getBudgetComponent(key)}
            </Typography>
          </BudgetBlock>
        ))}
      </>
    );
  },
);

const BudgetBlock = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(0.5)};
  text-align: right;
  white-space: nowrap;
`;

export default BudgetList;
